import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { Factor } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { MFA_FRIENDLY_NAME, MFA_ISSUER, rememberMfaDevice, type AalLevel } from '@/lib/mfa';

export interface EnrollResult {
  factorId: string;
  /** SVG data-URI produced by Supabase — render directly in an <img>. */
  qrCode: string;
  /** Base32 secret, for authenticator apps entered by hand. */
  secret: string;
}

interface MfaContextType {
  /** Assurance level of the *current* session. */
  currentLevel: AalLevel;
  /** Assurance level the user *could* reach — 'aal2' iff a verified factor exists. */
  nextLevel: AalLevel;
  loading: boolean;
  /** null until refreshFactors() has run at least once. */
  factors: Factor[] | null;
  refreshFactors: () => Promise<Factor[]>;
  enroll: () => Promise<EnrollResult>;
  verify: (factorId: string, code: string) => Promise<void>;
  unenroll: (factorId: string) => Promise<void>;
}

const MfaContext = createContext<MfaContextType | undefined>(undefined);

export const MfaProvider = ({ children }: { children: ReactNode }) => {
  const { session, loading: authLoading } = useAuth();
  const [currentLevel, setCurrentLevel] = useState<AalLevel>(null);
  const [nextLevel, setNextLevel] = useState<AalLevel>(null);
  const [factors, setFactors] = useState<Factor[] | null>(null);
  // Which access token the levels above were derived from. Assurance level is a
  // claim *inside* the token, so keying on the token is exact: a new token (sign
  // in, refresh, or a completed challenge) always means the levels are stale.
  const [syncedToken, setSyncedToken] = useState<string | null>(null);

  const accessToken = session?.access_token ?? null;

  useEffect(() => {
    if (authLoading) return;

    if (!accessToken) {
      setCurrentLevel(null);
      setNextLevel(null);
      setFactors(null);
      setSyncedToken(null);
      return;
    }

    let cancelled = false;

    // getAuthenticatorAssuranceLevel decodes the locally stored JWT — no network
    // round-trip — so the route gate can consult it on every navigation for free.
    (async () => {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (cancelled) return;
      if (error) {
        console.error('Failed to read authenticator assurance level:', error.message);
      }
      setCurrentLevel((data?.currentLevel ?? null) as AalLevel);
      setNextLevel((data?.nextLevel ?? null) as AalLevel);
      setSyncedToken(accessToken);
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken, authLoading]);

  // Stay "loading" until the levels describe the *current* token. Without this
  // there is a tick after sign-in where the user exists but the gate would still
  // be reading null levels, and a half-authenticated session would slip through.
  const loading = authLoading || (accessToken !== null && syncedToken !== accessToken);

  const refreshFactors = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    // `all` is unfiltered; `totp` only contains verified factors.
    const all = data?.all ?? [];
    setFactors(all);
    return all;
  }, []);

  const enroll = useCallback(async (): Promise<EnrollResult> => {
    // A user who abandons setup leaves an unverified factor behind. Supabase
    // rejects a second enrollment under the same friendly name, so clear the
    // stale ones first — otherwise the retry fails instead of showing a new QR.
    const { data: existing } = await supabase.auth.mfa.listFactors();
    const stale = (existing?.all ?? []).filter((f) => f.status !== 'verified');
    for (const factor of stale) {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
      if (error) console.error('Failed to clear stale MFA factor:', error.message);
    }

    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      // friendlyName is only an API-side label; `issuer` is what the user actually
      // sees in Microsoft Authenticator. Without it Supabase falls back to the site
      // URL, so the entry reads "bigesales.com" instead of the product name.
      friendlyName: MFA_FRIENDLY_NAME,
      issuer: MFA_ISSUER,
    });
    if (error) throw error;

    await refreshFactors();

    return {
      factorId: data.id,
      qrCode: data.totp.qr_code,
      secret: data.totp.secret,
    };
  }, [refreshFactors]);

  const verify = useCallback(async (factorId: string, code: string) => {
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
    if (error) throw error;
    // Trust this browser for MFA_REMEMBER_HOURS so a later sign-in from here skips
    // the challenge (see the gate in ProtectedRoute).
    const userId = session?.user?.id;
    if (userId) rememberMfaDevice(userId);
    // Supabase issues a fresh aal2 access token here, which flows through useAuth
    // and re-triggers the sync effect above.
    await refreshFactors();
  }, [refreshFactors, session]);

  const unenroll = useCallback(async (factorId: string) => {
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) throw error;
    await refreshFactors();
  }, [refreshFactors]);

  return (
    <MfaContext.Provider
      value={{ currentLevel, nextLevel, loading, factors, refreshFactors, enroll, verify, unenroll }}
    >
      {children}
    </MfaContext.Provider>
  );
};

export const useMfa = () => {
  const context = useContext(MfaContext);
  if (context === undefined) {
    throw new Error('useMfa must be used within an MfaProvider');
  }
  return context;
};
